import { PLAYER_HEIGHT, PLAYER_RADIUS, STEP_HEIGHT } from "../config";
import { isSolid } from "./blocks";
import type { World } from "./world";

export interface Aabb {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

export function playerBox(x: number, y: number, z: number): Aabb {
  return {
    minX: x - PLAYER_RADIUS,
    maxX: x + PLAYER_RADIUS,
    minY: y,
    maxY: y + PLAYER_HEIGHT,
    minZ: z - PLAYER_RADIUS,
    maxZ: z + PLAYER_RADIUS,
  };
}

export function overlapsSolid(world: World, box: Aabb): boolean {
  const x0 = Math.floor(box.minX);
  const y0 = Math.floor(box.minY);
  const z0 = Math.floor(box.minZ);
  const x1 = Math.floor(box.maxX);
  const y1 = Math.floor(box.maxY);
  const z1 = Math.floor(box.maxZ);
  for (let y = y0; y <= y1; y++) {
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) {
        if (isSolid(world.getBlock(x, y, z))) {
          const b: Aabb = { minX: x, maxX: x + 1, minY: y, maxY: y + 1, minZ: z, maxZ: z + 1 };
          if (aabbOverlap(box, b)) return true;
        }
      }
    }
  }
  return false;
}

export function aabbOverlap(a: Aabb, b: Aabb): boolean {
  return a.minX < b.maxX && a.maxX > b.minX && a.minY < b.maxY && a.maxY > b.minY && a.minZ < b.maxZ && a.maxZ > b.minZ;
}

export function moveWithCollisions(
  world: World,
  x: number,
  y: number,
  z: number,
  dx: number,
  dy: number,
  dz: number,
): { x: number; y: number; z: number; grounded: boolean; hitX: boolean; hitY: boolean; hitZ: boolean } {
  let hitX = false;
  let hitY = false;
  let hitZ = false;

  y += dy;
  if (overlapsSolid(world, playerBox(x, y, z))) {
    const sign = Math.sign(dy) || -1;
    while (overlapsSolid(world, playerBox(x, y, z))) y -= sign * 0.01;
    hitY = true;
  }

  const tryStep = Math.abs(dx) + Math.abs(dz) > 0 && hitY === false;
  x += dx;
  if (overlapsSolid(world, playerBox(x, y, z))) {
    if (tryStep || !hitY) {
      let stepped = false;
      if (dy <= 0) {
        for (let step = 0.1; step <= STEP_HEIGHT; step += 0.1) {
          if (!overlapsSolid(world, playerBox(x, y + step, z))) {
            y += step;
            stepped = true;
            break;
          }
        }
      }
      if (!stepped) {
        const sign = Math.sign(dx) || 1;
        while (overlapsSolid(world, playerBox(x, y, z))) x -= sign * 0.01;
        hitX = true;
      }
    } else {
      const sign = Math.sign(dx) || 1;
      while (overlapsSolid(world, playerBox(x, y, z))) x -= sign * 0.01;
      hitX = true;
    }
  }

  z += dz;
  if (overlapsSolid(world, playerBox(x, y, z))) {
    let stepped = false;
    if (dy <= 0 && !hitX) {
      for (let step = 0.1; step <= STEP_HEIGHT; step += 0.1) {
        if (!overlapsSolid(world, playerBox(x, y + step, z))) {
          y += step;
          stepped = true;
          break;
        }
      }
    }
    if (!stepped) {
      const sign = Math.sign(dz) || 1;
      while (overlapsSolid(world, playerBox(x, y, z))) z -= sign * 0.01;
      hitZ = true;
    }
  }

  const grounded = overlapsSolid(world, playerBox(x, y - 0.08, z)) && dy <= 0;
  return { x, y, z, grounded, hitX, hitY, hitZ };
}

export function boxOverlapsWater(world: World, box: Aabb): boolean {
  const x0 = Math.floor(box.minX);
  const y0 = Math.floor(box.minY);
  const z0 = Math.floor(box.minZ);
  const x1 = Math.floor(box.maxX);
  const y1 = Math.floor(box.maxY);
  const z1 = Math.floor(box.maxZ);
  for (let y = y0; y <= y1; y++) {
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) {
        if (world.isWater(x, y, z)) return true;
      }
    }
  }
  return false;
}
